/**
 * « Traduire dans ma langue » and « Écouter l'explication » of a lesson (D-107). The translation
 * is made once by the AI on the server and kept; the page asks for it, then reads it again while it
 * is being made. It is shown with a « traduction automatique (IA) » badge; a beta language says so.
 * Without AI, the existing help (English or French) is shown with the server's message.
 * The explanation is read by the server's voice, else the device's, else in French or English,
 * and the page says honestly which one it used.
 */

import {
  academyCourseLessonsListenCreate,
  academyCourseLessonsTranslationCreate,
  getAcademyCourseLessonsTranslationRetrieveQueryKey,
  useAcademyCourseLessonsTranslationRetrieve,
} from "@agencyos/api-client";
import { type LessonTranslation, type SpokenLanguage, languageLabel, parseListen, parseTranslation } from "@agencyos/core";
import { Badge, Button, GlassCard } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { Headphones, Languages, Square } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { stopSpeaking } from "../audio";
import { useLessonExamParams } from "./exam";
import { AlwaysInMyLanguage, useMotherTongue } from "./InMyLanguage";
import { Markdown } from "./Markdown";
import { LanguageNote, MotherTongueSelect, useLanguages } from "./MotherTongue";
import { LessonPitfalls } from "./Pitfalls";
import { playExplanation } from "./voice";

const POLL_MS = 2500;
const POLL_MAX = 24; // about one minute, then the page stops asking

/** The kept translation of a lesson in a language (read again while it is being made). */
export function useLessonTranslation(lessonId: string, language: string, asked: boolean) {
  const [polls, setPolls] = useState(0);
  const examParams = useLessonExamParams(); // D-109: the TEF path's shared lessons
  const query = useAcademyCourseLessonsTranslationRetrieve(
    lessonId,
    { language, ...examParams },
    {
      query: {
        enabled: Boolean(language) && asked,
        retry: false,
        staleTime: POLL_MS, // the reply of « Translate » is shown at once, then read again if pending
        refetchInterval: (q) => {
          const data = q.state.data;
          const pending = data?.status === 200 && parseTranslation(data.data).status === "pending";
          return pending && polls < POLL_MAX ? POLL_MS : false;
        },
      },
    },
  );
  useEffect(() => {
    if (query.isFetchedAfterMount) setPolls((n) => n + 1);
  }, [query.dataUpdatedAt, query.isFetchedAfterMount]);
  return useMemo(() => (query.data?.status === 200 ? parseTranslation(query.data.data) : null), [query.data]);
}

function TranslationView({ translation, language }: { translation: LessonTranslation; language: SpokenLanguage | undefined }) {
  const { t, i18n } = useTranslation();
  const ai = translation.source === "ai";
  return (
    <div className="ac-translation" lang={language?.code} dir={language?.rtl ? "rtl" : undefined}>
      <div className="ac-translation__head">
        {ai && <Badge tone="info">{t("academyCourse.translate.badge")}</Badge>}
        {translation.beta && <Badge tone="warning">{t("academyCourse.translate.beta")}</Badge>}
        {language && <span className="ac-caption">{languageLabel(language, i18n.language)}</span>}
      </div>
      {translation.message && (
        <p className="ac-note" role="note">
          {translation.message}
        </p>
      )}
      {translation.explanation ? <Markdown source={translation.explanation} lang={language?.code ?? translation.language} /> : null}
      {translation.objectives.length > 0 && (
        <>
          <p className="ac-label">{t("ielts.lesson.objectives")}</p>
          <ul className="ac-checklist">
            {translation.objectives.map((o) => (
              <li key={o}>{o}</li>
            ))}
          </ul>
        </>
      )}
      {Object.keys(translation.vocabulary).length > 0 && (
        <>
          <p className="ac-label">{t("ielts.lesson.vocabulary")}</p>
          <dl className="ac-translation__vocab">
            {Object.entries(translation.vocabulary).map(([word, meaning]) => (
              <div key={word}>
                <dt>{word}</dt>
                <dd>{meaning}</dd>
              </div>
            ))}
          </dl>
        </>
      )}
    </div>
  );
}

export function LessonHelp({ lessonId, motherTongue, onTranslation }: { lessonId: string; motherTongue: string; onTranslation: (t: LessonTranslation | null) => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const languages = useLanguages();
  const [code, setCode] = useState(motherTongue);
  const [asked, setAsked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceNote, setVoiceNote] = useState("");
  const audio = useRef<HTMLAudioElement>(null);
  const translation = useLessonTranslation(lessonId, code, asked);
  const examParams = useLessonExamParams();
  const language = languages.find((l) => l.code === code);

  const { always } = useMotherTongue();
  const auto = useRef(false);

  useEffect(() => setCode(motherTongue), [motherTongue]);
  useEffect(() => onTranslation(asked ? translation : null), [asked, translation, onTranslation]);
  useEffect(() => () => stopSpeaking(), []);

  const translate = async () => {
    if (!code) return;
    setBusy(true);
    try {
      const reply = await academyCourseLessonsTranslationCreate(lessonId, { language: code }, examParams);
      queryClient.setQueryData(getAcademyCourseLessonsTranslationRetrieveQueryKey(lessonId, { language: code, ...examParams }), reply);
      setAsked(true);
    } finally {
      setBusy(false);
    }
  };

  // « Toujours afficher dans ma langue » (D-122): the translation is asked by itself
  useEffect(() => {
    if (always && motherTongue && !auto.current) {
      auto.current = true;
      void translate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [always, motherTongue]);

  const listen = async () => {
    if (!code) return;
    setListening(true);
    setVoiceNote("");
    try {
      let plan = parseListen((await academyCourseLessonsListenCreate(lessonId, { language: code }, examParams)).data);
      if (plan.mode === "server" && plan.status === "pending") {
        setVoiceNote(t("academyCourse.listen.preparing"));
        plan = { ...plan, mode: "device" }; // the device reads it now; the server's voice next time
      }
      const voice = await playExplanation(plan, audio.current);
      const notes = {
        server: t("academyCourse.listen.server"),
        device: "",
        fallback: t("academyCourse.listen.fallback"),
        none: t("academyCourse.listen.noVoice"),
      };
      // nothing can speak here: the server's note (if any) and why nothing is heard
      setVoiceNote(voice === "none" ? [plan.message, notes.none].filter(Boolean).join(" ") : plan.message || notes[voice]);
    } finally {
      setListening(false);
    }
  };

  const stop = () => {
    stopSpeaking();
    audio.current?.pause();
    setListening(false);
  };

  return (
    <GlassCard as="section" className="ac-card ac-lesson-help" aria-labelledby="ac-lesson-help">
      <h2 id="ac-lesson-help" className="ac-card-title">
        <span className="ac-pastille ac-tone--info" aria-hidden="true">
          <Languages size={16} />
        </span>
        <span>{t("academyCourse.translate.title")}</span>
      </h2>
      {!motherTongue && <MotherTongueSelect value={code} onChange={setCode} />}
      {motherTongue && <LanguageNote language={language} />}
      <div className="ac-card__actions ac-card__actions--start">
        <Button variant="tinted" icon={<Languages aria-hidden size={15} />} disabled={!code || busy} onClick={() => void translate()}>
          {busy ? t("common.loading") : t("academyCourse.translate.action")}
        </Button>
        {listening ? (
          <Button variant="secondary" icon={<Square aria-hidden size={14} />} onClick={stop}>
            {t("academyCourse.listen.stop")}
          </Button>
        ) : (
          <Button variant="ghost" icon={<Headphones aria-hidden size={15} />} disabled={!code} onClick={() => void listen()}>
            {t("academyCourse.listen.action")}
          </Button>
        )}
      </div>
      {/* The explanation read by the server's voice (no caption: the same text is shown above). */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audio} hidden onEnded={() => setListening(false)} />
      {voiceNote && (
        <p className="ac-footnote" role="status">
          {voiceNote}
        </p>
      )}
      {asked && translation?.status === "pending" && (
        <p className="ac-status" role="status">
          {t("academyCourse.translate.pending")}
        </p>
      )}
      {asked && translation && translation.status !== "pending" && translation.status !== "none" && <TranslationView translation={translation} language={language} />}
      {motherTongue && <LessonPitfalls lessonId={lessonId} code={motherTongue} language={language} auto={always} />}
      {motherTongue && <AlwaysInMyLanguage />}
      <p className="ac-footnote">{t("academyCourse.translate.examStays")}</p>
    </GlassCard>
  );
}
